import { useReducer } from 'react'

import { COMPARE_ARRAY_PARAMS, type FeatureMode } from '@/lib/directory/compare-url'

export enum SortOption {
  Featured = 'featured',
  TopRated = 'score',
  PriceLow = 'price_low',
  PriceHigh = 'price_high',
  Newest = 'newest',
}

export interface DirectoryFilters {
  classification: string[]
  tags: string[]
  channels: string[]
  accessories: string[]
  hardware: string[]
  software: string[]
  loss: string[]
  style: string[]
  brand: string
  featureMode: FeatureMode
}

// The compare selection is NOT here: it lives in the shared `useCompared`
// store (localStorage `ht-comparisons`), so brand pages and the directory
// agree and the four-pick cap holds on rehydrate.
export interface DirectoryState {
  filters: DirectoryFilters
  sort: SortOption
  term: string
  /** 1-based results page; any change to filters, sort or term returns to 1. */
  page: number
  isFilterModalOpen: boolean
}

export enum ActionType {
  ToggleFilter = 'TOGGLE_FILTER',
  RadioFilter = 'RADIO_FILTER',
  SetBrand = 'SET_BRAND',
  SetFeatureMode = 'SET_FEATURE_MODE',
  SetSort = 'SET_SORT',
  SetTerm = 'SET_TERM',
  SetPage = 'SET_PAGE',
  ToggleModal = 'TOGGLE_MODAL',
  ClearFilters = 'CLEAR_FILTERS',
  ClearFilterKey = 'CLEAR_FILTER_KEY',
  HydrateFromQuery = 'HYDRATE_FROM_QUERY',
}

export type FilterArrayKey = Exclude<keyof DirectoryFilters, 'brand' | 'featureMode'>

export type DirectoryAction =
  | { type: ActionType.ToggleFilter; key: FilterArrayKey; value: string }
  | { type: ActionType.RadioFilter; key: FilterArrayKey; value: string }
  | { type: ActionType.SetBrand; value: string }
  | { type: ActionType.SetFeatureMode; value: FeatureMode }
  | { type: ActionType.SetSort; sort: SortOption }
  | { type: ActionType.SetTerm; term: string }
  | { type: ActionType.SetPage; page: number }
  | { type: ActionType.ToggleModal }
  | { type: ActionType.ClearFilters }
  | { type: ActionType.ClearFilterKey; key: FilterArrayKey | 'brand' }
  | { type: ActionType.HydrateFromQuery; filters?: Partial<DirectoryFilters>; sort?: SortOption; term?: string }

export const INITIAL_FILTERS: DirectoryFilters = {
  classification: [],
  tags: [],
  channels: [],
  accessories: [],
  hardware: [],
  software: [],
  loss: [],
  style: [],
  brand: '',
  featureMode: 'and',
}

const INITIAL_STATE: DirectoryState = {
  filters: INITIAL_FILTERS,
  sort: SortOption.Featured,
  term: '',
  page: 1,
  isFilterModalOpen: false,
}

const toggleArrayValue = (arr: string[], value: string): string[] => {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

const setFilterValues = (filters: DirectoryFilters, key: FilterArrayKey, values: string[]): DirectoryFilters => ({
  ...filters,
  [key]: values,
})

// Every action that changes which rows show (filters, sort, search) also
// returns to page 1; only SetPage and the sheet toggle leave the page alone.
const directoryReducer = (state: DirectoryState, action: DirectoryAction): DirectoryState => {
  switch (action.type) {
    case ActionType.ToggleFilter:
      return {
        ...state,
        page: 1,
        filters: setFilterValues(state.filters, action.key, toggleArrayValue(state.filters[action.key], action.value)),
      }
    case ActionType.RadioFilter:
      return {
        ...state,
        page: 1,
        filters: setFilterValues(
          state.filters,
          action.key,
          state.filters[action.key].includes(action.value) ? [] : [action.value]
        ),
      }
    case ActionType.SetFeatureMode:
      return { ...state, page: 1, filters: { ...state.filters, featureMode: action.value } }
    case ActionType.SetBrand:
      return {
        ...state,
        page: 1,
        filters: {
          ...state.filters,
          brand: state.filters.brand === action.value ? '' : action.value,
        },
      }
    case ActionType.SetSort:
      return { ...state, page: 1, sort: action.sort }
    case ActionType.SetTerm:
      return { ...state, page: 1, term: action.term }
    case ActionType.SetPage:
      return { ...state, page: Math.max(1, Math.floor(action.page) || 1) }
    case ActionType.ToggleModal:
      return { ...state, isFilterModalOpen: !state.isFilterModalOpen }
    case ActionType.ClearFilters:
      return { ...state, page: 1, filters: INITIAL_FILTERS, term: '' }
    case ActionType.ClearFilterKey:
      if (action.key === 'brand') {
        return { ...state, page: 1, filters: { ...state.filters, brand: '' } }
      }
      return { ...state, page: 1, filters: setFilterValues(state.filters, action.key, []) }
    case ActionType.HydrateFromQuery:
      return {
        ...state,
        page: 1,
        filters: { ...state.filters, ...action.filters },
        sort: action.sort ?? state.sort,
        term: action.term ?? state.term,
      }
  }
}

// Compile-time guard: every deep-link query param must be a real array filter
// axis. If COMPARE_ARRAY_PARAMS drifts from DirectoryFilters, this errors.
const _COMPARE_PARAMS_ARE_AXES: readonly FilterArrayKey[] = COMPARE_ARRAY_PARAMS

/** Number of active filter values (the badge on the mobile Filters button). */
export const countActiveFilters = (filters: DirectoryFilters): number =>
  (filters.brand ? 1 : 0) + COMPARE_ARRAY_PARAMS.reduce((sum, key) => sum + filters[key].length, 0)

export interface HydratePayload {
  filters?: Partial<DirectoryFilters>
  sort?: SortOption
  term?: string
}

const splitParamValues = (raw: string): string[] =>
  raw
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0)

/**
 * Parse a directory deep-link query string (`window.location.search`) into a
 * HydrateFromQuery payload. Reads only known filter axes + `brand`/`sort`/`term`;
 * comma-separated values become arrays; unknown keys are ignored. Pure — safe to
 * unit-test and to call from a mount effect.
 */
export const parseDirectoryQuery = (search: string): HydratePayload => {
  const params = new URLSearchParams(search)
  const filters: Partial<DirectoryFilters> = {}

  for (const key of COMPARE_ARRAY_PARAMS) {
    const raw = params.get(key)
    if (raw == null) {
      continue
    }
    const values = splitParamValues(raw)
    if (values.length > 0) {
      filters[key as FilterArrayKey] =
        key === 'tags'
          ? [...new Set(values.map((value) => (value === 'Public Access' ? 'Public Hearing Access' : value)))]
          : values
    }
  }

  const brand = params.get('brand')?.trim()
  if (brand) {
    filters.brand = brand
  }

  const featureMode = params.get('featureMode')
  if (featureMode === 'and' || featureMode === 'or') {
    filters.featureMode = featureMode
  }

  const payload: HydratePayload = {}
  if (Object.keys(filters).length > 0) {
    payload.filters = filters
  }

  const sort = params.get('sort')
  if (sort && (Object.values(SortOption) as string[]).includes(sort)) {
    payload.sort = sort as SortOption
  }

  const term = params.get('term')?.trim()
  if (term) {
    payload.term = term
  }

  return payload
}

export const useDirectoryReducer = () => {
  return useReducer(directoryReducer, INITIAL_STATE)
}
